"use client"

import * as React from "react"
import { type ColumnDef } from "@tanstack/react-table"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ChevronDownIcon, LandmarkIcon, PlusIcon, XIcon } from "lucide-react"
import { toast } from "sonner"

import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  HelperMessage,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  Select,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from "@ibada/ui"

import { useLocale, useTranslations } from "@/i18n"
import { AliveLeadCell, AliveMark } from "@/shared/components/alive-cells"
import { ExportAction, exportFileName } from "@/shared/components/export"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import {
  FinancePeriodPicker,
  MOBILE_SUBTITLE_PERIOD_PICKER_CLASS,
} from "@/features/finance/components/finance-period-picker"
import { ReconciliationStatusBadge } from "@/features/finance/components/reconciliation/reconciliation-status-badge"
import { formatMoney, getCurrencyPrefix, toMoneyOrNull } from "@/features/finance/lib/finance-utils"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { ReconciliationSessionStatus } from "@/features/finance/model/finance-close-trust-types"
import type { ReconciliationSessionSummary } from "@/features/finance/model/finance-close-trust-types"
import {
  useCreateReconciliationSessionMutation,
  useReconciliationSessionsQuery,
} from "@/features/finance/services/finance-close-trust-query"
import { useFinanceAccountsQuery } from "@/features/finance/services/finance-query"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { PeriodPreset, resolvePeriodRange } from "@/shared/lib/dates/period-range"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { datePickerValueToDateOnly, dbDateToDateOnly } from "@ibada/shared-types"
import { isZeroMoney } from "@ibada/shared-types/finance"
import {
  DataTableMobileListVariant,
  type DataTableAction,
  type DataTableToolbarActionInput, DataTableVariant } from "@ibada/ui/components/data-table"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import {
  MobileFormAmountRow,
  MobileFormDateRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobilePickerSheet,
  useFormatMoneyText,
} from "@/shared/components/mobile-form"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useDataTableToolbarConfig } from "@/shared/lib/data-table/use-data-table-toolbar-config"

const todayDateOnly = () => datePickerValueToDateOnly(new Date()) ?? ""
const pickerDateOnly = (value: Date) => datePickerValueToDateOnly(value) ?? ""
const displayDateOnly = (value: string) => dbDateToDateOnly(value) ?? value.slice(0, 10)

/** Sentinel for the "no filter" option — an empty string reads as "nothing selected", not "everything". */
const ALL = "__all__"

/**
 * Every reconciliation in the scope, across all accounts.
 *
 * Session-centric rather than account-first on purpose: the person most likely to open this page is a
 * reviewer asking "what is waiting for me", and an account-first launcher makes them guess which account
 * holds it. The account is a column instead.
 */
export function ReconciliationListContent() {
  const t = useTranslations("finance.reconciliation")
  const tCommon = useTranslations("common")
  const toastError = useApiErrorToast("finance")
  const navigate = useNavigate()
  const { workspaceId, activeContext, scopeBranch, parishBranch, canEditFinance } = useFinancePageState()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const currency = useBranchCurrency()

  const branchId = scopeBranch?.id ?? null
  const scope = activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish
  const scopedBranchSlug = activeContext === "parish" ? (parishBranch?.slug ?? branchSlug ?? "") : (branchSlug ?? "")

  // In the URL, not useState: this is a tab strip, and a reviewer who opens a session from the queue and
  // presses Back must land back on the queue — not on "All statuses" with their filter thrown away. It is a
  // query param rather than a path segment because `/finance/reconciliation/:sessionId` already owns the
  // next segment; a status segment there would be ambiguous with a session id.
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = searchParams.get("status") ?? ALL
  const accountFilter = searchParams.get("account") ?? ALL

  const setParam = React.useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value === ALL) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )
  const setStatusFilter = React.useCallback((value: string) => setParam("status", value), [setParam])
  const setAccountFilter = React.useCallback((value: string) => setParam("account", value), [setParam])

  // The period window, also in the URL so a filtered queue stays linkable and survives Back.
  const dateFrom = searchParams.get("from")
  const dateTo = searchParams.get("to")
  const hasDateFilter = Boolean(dateFrom && dateTo)

  const applyPreset = React.useCallback(
    (next: PeriodPreset) => {
      const range = resolvePeriodRange(next, { now: new Date() })
      if (!range) return
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current)
          params.set("preset", next)
          params.set("from", pickerDateOnly(range.start))
          params.set("to", pickerDateOnly(range.end))
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const clearDateFilter = React.useCallback(() => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.delete("preset")
        params.delete("from")
        params.delete("to")
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])
  const [createOpen, setCreateOpen] = React.useState(false)

  const sessionsQuery = useReconciliationSessionsQuery(
    workspaceId && (activeContext === "parish" || branchId)
      ? {
          workspaceId,
          branchId,
          scope,
          ...(accountFilter !== ALL ? { financeAccountId: accountFilter } : {}),
          ...(statusFilter !== ALL ? { status: statusFilter as ReconciliationSessionStatus } : {}),
          ...(hasDateFilter ? { dateFrom: dateFrom as string, dateTo: dateTo as string } : {}),
        }
      : null,
  )

  const accountsQuery = useFinanceAccountsQuery(
    workspaceId && (activeContext === "parish" || branchId) ? { workspaceId, branchId, scope } : null,
  )

  const sessions = sessionsQuery.data?.items ?? []
  // The server's unfiltered queue depth (ADR-0011) — so the badge stays honest while the list is filtered,
  // including when the user is looking at a single account or another status entirely.
  const pendingReviewCount = sessionsQuery.data?.pendingReviewCount ?? 0
  // "Nothing here" and "nothing MATCHES" are different situations and say so — but neither needs its own
  // button: the status tabs and the account select are both one row above, already showing what is applied.
  const hasFilter = statusFilter !== ALL || accountFilter !== ALL || hasDateFilter
  const accounts = accountsQuery.data?.items ?? []
  const sessionUrl = (id: string) =>
    buildBranchAdminUrl(scopedBranchSlug, `/finance/reconciliation/${id}`, workspaceSlug)

  const sessionColumns = React.useMemo<ColumnDef<ReconciliationSessionSummary>[]>(
    () => [
      {
        id: "account",
        accessorFn: (row) => row.financeAccount.name,
        header: t("sessions.columns.account"),
        size: 300,
        // The account leads, with the statement period under it; the row opens the session.
        cell: ({ row, getValue }) => (
          <AliveLeadCell
            leading={<AliveMark icon={LandmarkIcon} />}
            title={String(getValue())}
            subtitle={`${displayDateOnly(row.original.statementDateFrom)} → ${displayDateOnly(row.original.statementDateTo)}`}
          />
        ),
      },
      {
        id: "period",
        accessorFn: (row) => row.statementDateTo,
        header: t("sessions.columns.period"),
        // Already under the account; still in the column picker and sortable from the menu.
        meta: { defaultHidden: true },
        cell: ({ row }) => (
          <span className='text-muted-foreground tabular-nums'>
            {displayDateOnly(row.original.statementDateFrom)} → {displayDateOnly(row.original.statementDateTo)}
          </span>
        ),
      },
      {
        id: "difference",
        // Sorted as a NUMBER: these are canonical decimal strings (IBA-116), and sorting them lexically puts
        // "9.00" above "10.00" — on a reconciliation list that reads as the wrong account needing attention.
        accessorFn: (row) => (row.difference === null ? 0 : Number(row.difference)),
        header: t("sessions.columns.difference"),
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className='text-[13px] font-extrabold tabular-nums'>
            {/* Withheld under branch autonomy (ADR-0024): the reconciliation happened and the parish may know
                that, but the figure is the branch's. A dash, never a zero (see the type). */}
            {row.original.difference === null ? (
              <span className='text-muted-foreground font-semibold'>-</span>
            ) : (
              formatMoney(row.original.difference, row.original.currency ?? currency)
            )}
          </span>
        ),
      },
      {
        id: "status",
        accessorFn: (row) => row.status,
        header: t("sessions.columns.status"),
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className='flex justify-end'>
            <ReconciliationStatusBadge status={row.original.status} />
          </span>
        ),
      },
    ],
    [currency, t],
  )

  const isMobile = useIsMobile()
  const openCreate = React.useCallback(() => {
    if (!canEditFinance) return
    setCreateOpen(true)
  }, [canEditFinance])

  // Phone: the list's toolbar draws the title row (Start and ⋯ beside it, export folded into ⋯), the period and
  // the account as two quiet pickers under the title (no filter button on a row of its own), and the statuses
  // as chips.
  const [accountSheetOpen, setAccountSheetOpen] = React.useState(false)
  const accountFilterLabel =
    accountFilter === ALL
      ? t("sessions.filters.account")
      : (accounts.find((account) => account.id === accountFilter)?.name ?? t("sessions.filters.account"))
  const mobileToolbarBase = useDataTableToolbarConfig({
    // The account is picked under the title instead; `filters` is dropped below so no filter button shows.
    filterConfig: {},
    appliedFilters: {},
    onApplyFilters: () => undefined,
    onResetFilters: () => undefined,
    actions: [
      {
        id: "export",
        // Phone: a round download icon in the title row (it opens the formats in a sheet).
        mobileIconOnly: true,
        label: tCommon("export.button"),
        emphasis: "priority",
        order: 10,
        render: ({ inOverflow }) => (
          <ExportAction
            renderAs={inOverflow ? "menuItem" : "button"}
            itemCount={sessions.length}
            config={{
              resource: "reconciliations",
              fileName: exportFileName(t("title")),
              query: { workspaceId: workspaceId ?? undefined, branchId: branchId ?? undefined },
            }}
          />
        ),
      } satisfies DataTableAction,
      ...(canEditFinance
        ? [
            {
              id: "start",
              label: t("new.action"),
              icon: <PlusIcon className='size-4' />,
              emphasis: "primary",
              order: 20,
              disabled: accounts.length === 0,
              onSelect: openCreate,
            } satisfies DataTableAction,
          ]
        : []),
    ] satisfies DataTableToolbarActionInput[],
    onRefresh: () => sessionsQuery.refetch(),
    isRefreshing: sessionsQuery.isFetching,
  })
  const mobileToolbar = {
    ...mobileToolbarBase,
    mobileHeader: {
      title: t("sessions.title"),
      subtitle: (
        <span className='flex min-w-0 items-center gap-1.5'>
          <FinancePeriodPicker
            value={(searchParams.get("preset") as PeriodPreset | null) ?? PeriodPreset.ThisMonth}
            onValueChange={applyPreset}
            isCustom={!hasDateFilter}
            customLabel={t("sessions.filters.anyTime")}
            hideLabel
            className={MOBILE_SUBTITLE_PERIOD_PICKER_CLASS}
          />
          {hasDateFilter ? (
            <button
              type='button'
              aria-label={t("sessions.filters.clearPeriod")}
              onClick={clearDateFilter}
              className='inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground active:bg-muted/70'
            >
              <XIcon aria-hidden className='size-3' />
            </button>
          ) : null}
          <span aria-hidden className='shrink-0'>·</span>
          <button
            type='button'
            onClick={() => setAccountSheetOpen(true)}
            className='inline-flex h-7 min-w-0 items-center gap-1.5 text-[13px] font-bold text-muted-foreground'
          >
            <span className='truncate'>{accountFilterLabel}</span>
            <ChevronDownIcon aria-hidden className='size-4 shrink-0 opacity-60' />
          </button>
        </span>
      ),
    },
    filters: undefined,
    views: {
      value: statusFilter,
      onValueChange: setStatusFilter,
      items: [
        { value: ALL, label: t("sessions.filters.all") },
        ...Object.values(ReconciliationSessionStatus).map((status) => ({
          value: status,
          label:
            status === ReconciliationSessionStatus.PendingReview && pendingReviewCount > 0
              ? `${t(STATUS_LABEL_KEY[status])} · ${pendingReviewCount}`
              : t(STATUS_LABEL_KEY[status]),
        })),
      ],
    },
  }

  const emptyMessage = sessionsQuery.isError
    ? t("sessions.error")
    : hasFilter
      ? t("sessions.emptyFiltered")
      : t("sessions.empty")

  const createDialog = (
    <CreateReconciliationDialog
      open={createOpen}
      onOpenChange={setCreateOpen}
      accounts={accounts}
      currency={currency}
      onCreated={(id) => navigate(sessionUrl(id))}
      createInput={{ workspaceId: workspaceId ?? "", branchId, scope }}
      onError={toastError}
      canEdit={canEditFinance}
    />
  )

  if (isMobile) {
    return (
      <div className='space-y-4'>
        <DataTable
          data={sessions}
          columns={sessionColumns}
          isLoading={sessionsQuery.isLoading}
          paginationRole='local'
          initialPagination={{ pageIndex: 0, pageSize: 25 }}
          mobileListVariant={DataTableMobileListVariant.Grouped}
          renderMobileRow={(row) => <ReconciliationMobileRow session={row.original} currency={currency} />}
          onRowClick={(row) => navigate(sessionUrl(row.id))}
          emptyMessage={emptyMessage}
          toolbar={mobileToolbar}
        />
        <MobilePickerSheet
          open={accountSheetOpen}
          onOpenChange={setAccountSheetOpen}
          title={t("new.account")}
          doneLabel={t("mobile.done")}
          emptyLabel={t("mobile.noResults")}
          value={accountFilter === ALL ? undefined : accountFilter}
          options={accounts.map((account) => ({ value: account.id, label: account.name }))}
          onSelect={setAccountFilter}
          clearLabel={t("sessions.filters.account")}
          onClear={() => setAccountFilter(ALL)}
        />
        {createDialog}
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {/* No page heading here: FinanceLayout renders the title + description for every finance section, and
          Reconciliation is now a registered tab. A second hand-rolled one would read as a different product.

          The tabs get their OWN full-width row. The `line` variant is a band with a bottom border, so any
          control sharing its row ends up sitting on that border — which is what made this look broken. */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value={ALL}>{t("sessions.filters.all")}</TabsTrigger>
          {Object.values(ReconciliationSessionStatus).map((status) => (
            <TabsTrigger key={status} value={status}>
              {t(STATUS_LABEL_KEY[status])}
              {/* The count belongs ON the tab it describes: "3 awaiting review" printed elsewhere makes you
                  map a number onto a tab yourself. Only ever on the review queue — that is the one state
                  where the number means work is waiting on somebody. */}
              {status === ReconciliationSessionStatus.PendingReview && pendingReviewCount > 0 ? (
                <span className='bg-primary text-primary-foreground ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold tabular-nums'>
                  {pendingReviewCount}
                </span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Toolbar: refine on the left, the one thing that creates work on the right. */}
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <Select
          size='sm'
          className='w-52'
          value={accountFilter}
          onValueChange={setAccountFilter}
          options={[
            { value: ALL, label: t("sessions.filters.account") },
            ...accounts.map((account) => ({ value: account.id, label: account.name })),
          ]}
        />

        {/* Filters by the period a session COVERS. It starts OFF: this is a review queue, and silently
            hiding a submission from an earlier period behind a default window is how work goes unnoticed. */}
        <FinancePeriodPicker
          value={(searchParams.get("preset") as PeriodPreset | null) ?? PeriodPreset.ThisMonth}
          onValueChange={applyPreset}
          isCustom={!hasDateFilter}
          customLabel={t("sessions.filters.anyTime")}
        />
        {hasDateFilter ? (
          <Button
            size='icon-sm'
            variant='ghost'
            aria-label={t("sessions.filters.clearPeriod")}
            title={t("sessions.filters.clearPeriod")}
            onClick={clearDateFilter}
          >
            <XIcon className='size-3.5' />
          </Button>
        ) : null}

        {/* On the filter row, not a row of its own: it refines the same list the controls to its left do. */}
        <div className='ml-auto flex items-center gap-2'>
          <ExportAction
            buttonSize='sm'
            itemCount={sessions.length}
            config={{
              resource: "reconciliations",
              fileName: exportFileName(t("title")),
              query: { workspaceId: workspaceId ?? undefined, branchId: branchId ?? undefined },
            }}
          />
          {canEditFinance ? (
            <Button
              size='sm'
              onClick={() => {
                if (!canEditFinance) return
                setCreateOpen(true)
              }}
              disabled={accounts.length === 0}
            >
          <PlusIcon className='size-3.5' />
            {t("new.action")}
            </Button>
          ) : null}
        </div>
      </div>

      <DataTable
        data={sessions}
        columns={sessionColumns}
        variant={DataTableVariant.Alive}
        isLoading={sessionsQuery.isLoading}
        paginationRole='local'
        initialPagination={{ pageIndex: 0, pageSize: 25 }}
        // Every account × every closed period: this grows without bound, so it gets the same table the rest
        // of finance uses rather than an unbounded list.
        onRowClick={(row) => navigate(sessionUrl(row.id))}
        // An error must not read as an empty queue: "No reconciliations yet" told a user who got a 403 that
        // there was no work, which is the most misleading thing this page could say.
        emptyMessage={emptyMessage}
      />

      {createDialog}
    </div>
  )
}

/**
 * One reconciliation on a phone: the account, its status and statement period, and the difference on the right
 * (green when the books match, amber while they do not). A difference withheld under branch autonomy is left
 * out rather than shown as a zero.
 */
function ReconciliationMobileRow({ session, currency }: { session: ReconciliationSessionSummary; currency: string }) {
  const sessionCurrency = session.currency ?? currency
  const formatMoneyText = useFormatMoneyText(sessionCurrency)
  const difference = session.difference
  return (
    <div className='flex min-w-0 flex-1 items-center gap-3'>
      <span className='flex min-w-0 flex-1 flex-col gap-1'>
        <span className='truncate text-[15px] leading-tight font-extrabold text-foreground'>{session.financeAccount.name}</span>
        <span className='flex min-w-0 items-center gap-1.5 text-xs font-semibold text-muted-foreground'>
          <span className='shrink-0'>
            <ReconciliationStatusBadge status={session.status} />
          </span>
          <span className='min-w-0 truncate tabular-nums'>
            {displayDateOnly(session.statementDateFrom)} → {displayDateOnly(session.statementDateTo)}
          </span>
        </span>
      </span>
      {difference !== null ? (
        <span
          className={cn(
            "max-w-[40%] shrink-0 truncate text-right text-[15px] font-extrabold tabular-nums",
            isZeroMoney(difference, sessionCurrency) ? "text-primary" : "text-amber-600 dark:text-amber-400",
          )}
        >
          {formatMoneyText(difference)}
        </span>
      ) : null}
    </div>
  )
}

const STATUS_LABEL_KEY: Record<ReconciliationSessionStatus, string> = {
  [ReconciliationSessionStatus.Draft]: "status.draft",
  [ReconciliationSessionStatus.PendingReview]: "status.pendingReview",
  [ReconciliationSessionStatus.Approved]: "status.approved",
  [ReconciliationSessionStatus.Rejected]: "status.rejected",
}

/** "1 Sep" from a "yyyy-mm-dd" day, read as that calendar day (never shifted by the device's timezone). */
function shortDay(day: string, locale: string): string {
  const [year, month, date] = day.split("-").map((part) => Number.parseInt(part, 10))
  if (!year || !month || !date) return day
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, date)),
  )
}

/** The statement periods offered on a phone, most common first; "custom" reveals the two date rows. */
const MOBILE_STATEMENT_PERIODS: PeriodPreset[] = [
  PeriodPreset.LastMonth,
  PeriodPreset.ThisMonth,
  PeriodPreset.LastQuarter,
  PeriodPreset.ThisQuarter,
  PeriodPreset.LastYear,
  PeriodPreset.Custom,
]

function CreateReconciliationDialog({
  open,
  onOpenChange,
  accounts,
  currency,
  createInput,
  onCreated,
  onError,
  canEdit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  accounts: Array<{ id: string; name: string }>
  currency: string
  createInput: { workspaceId: string; branchId: string | null; scope: FinanceCategoryScope }
  onCreated: (sessionId: string) => void
  onError: (error: unknown) => void
  canEdit: boolean
}) {
  const t = useTranslations("finance.reconciliation")
  const tPeriod = useTranslations("finance.period")
  const locale = useLocale()
  const isMobile = useIsMobile()
  const createSession = useCreateReconciliationSessionMutation()

  const [financeAccountId, setFinanceAccountId] = React.useState("")
  // Bank statements arrive per calendar month, so the period is chosen by PRESET first and typed only when
  // it genuinely differs. "Last month" is the common case — you reconcile the month that just closed —
  // so it is the default rather than today→today, which was never a statement period anybody has.
  const [preset, setPreset] = React.useState<PeriodPreset>(PeriodPreset.LastMonth)
  const [isCustomPeriod, setIsCustomPeriod] = React.useState(false)
  const [statementDateFrom, setStatementDateFrom] = React.useState(todayDateOnly())
  const [statementDateTo, setStatementDateTo] = React.useState(todayDateOnly())

  // A preset writes the two dates; custom mode leaves whatever the user typed alone.
  React.useEffect(() => {
    if (!open || isCustomPeriod) return
    const range = resolvePeriodRange(preset, { now: new Date() })
    if (!range) return
    setStatementDateFrom(pickerDateOnly(range.start))
    setStatementDateTo(pickerDateOnly(range.end))
  }, [open, isCustomPeriod, preset])
  const [statementOpeningBalance, setStatementOpeningBalance] = React.useState("")
  const [statementBalance, setStatementBalance] = React.useState("")

  React.useEffect(() => {
    if (!canEdit && open) onOpenChange(false)
  }, [canEdit, onOpenChange, open])

  React.useEffect(() => {
    if (!open) return
    setFinanceAccountId("")
    setPreset(PeriodPreset.LastMonth)
    setIsCustomPeriod(false)
    setStatementOpeningBalance("")
    setStatementBalance("")
  }, [open])

  // The bank statement's exact figure, kept as money all the way in — a reconciliation whose statement balance
  // has been through a float is one that can silently fail to reconcile (IBA-116). It may be NEGATIVE.
  const statementMoney = toMoneyOrNull(statementBalance, currency)
  // Optional by design: not every statement shows an opening figure, and inventing one would be worse than
  // leaving the check off. When present it unlocks "which period is the error in".
  const openingMoney = statementOpeningBalance.trim() ? toMoneyOrNull(statementOpeningBalance, currency) : null
  const canSubmit = Boolean(financeAccountId) && statementMoney !== null && statementDateFrom <= statementDateTo

  const handleCreate = async () => {
    if (!canEdit || !canSubmit || statementMoney === null) return
    try {
      const detail = await createSession.mutateAsync({
        ...createInput,
        financeAccountId,
        statementDateFrom,
        statementDateTo,
        statementBalance: statementMoney,
        ...(openingMoney ? { statementOpeningBalance: openingMoney } : {}),
      })
      toast.success(t("created"))
      onOpenChange(false)
      onCreated(detail.id)
    } catch (error) {
      onError(error)
    }
  }

  if (isMobile) {
    // Phone: a full-screen form of grouped rows. Full screen rather than a sheet because the account, the period
    // and the dates each open a sheet of their own, and a sheet never opens on a sheet.
    const picker = { doneLabel: t("mobile.done"), emptyLabel: t("mobile.noResults") }
    const periodLabel = (value: PeriodPreset) =>
      value === PeriodPreset.Custom ? tPeriod("custom") : tPeriod(`presets.${value}` as never)
    return (
      <MobileDetailScreen
        open={open && canEdit}
        onOpenChange={onOpenChange}
        title={t("new.title")}
        backLabel={t("dialogs.cancel")}
        showBackChevron={false}
        action={{
          label: t("new.action"),
          onPress: () => void handleCreate(),
          disabled: !canEdit || !canSubmit || createSession.isPending,
        }}
      >
        {accounts.length === 0 ? (
          <HelperMessage tone='info' size='sm'>
            {t("sessions.empty")}
          </HelperMessage>
        ) : null}

        <MobileFormSection>
          <MobileFormSelectRow
            {...picker}
            label={t("new.account")}
            placeholder={t("new.accountPlaceholder")}
            value={financeAccountId || undefined}
            onChange={(value) => setFinanceAccountId(value ?? "")}
            options={accounts.map((account) => ({ value: account.id, label: account.name }))}
          />
          <MobileFormSelectRow
            {...picker}
            label={t("new.period")}
            placeholder={t("new.period")}
            value={isCustomPeriod ? PeriodPreset.Custom : preset}
            valueLabel={periodLabel(isCustomPeriod ? PeriodPreset.Custom : preset)}
            sublabel={isCustomPeriod ? undefined : `${shortDay(statementDateFrom, locale)} → ${shortDay(statementDateTo, locale)}`}
            onChange={(value) => {
              if (value === PeriodPreset.Custom) {
                setIsCustomPeriod(true)
                return
              }
              if (!value) return
              setIsCustomPeriod(false)
              setPreset(value as PeriodPreset)
            }}
            options={MOBILE_STATEMENT_PERIODS.map((value) => ({ value, label: periodLabel(value) }))}
          />
          {isCustomPeriod ? (
            <>
              <MobileFormDateRow
                label={t("new.dateFrom")}
                value={statementDateFrom}
                onChange={setStatementDateFrom}
                placeholder={t("mobile.chooseDate")}
              />
              <MobileFormDateRow
                label={t("new.dateTo")}
                value={statementDateTo}
                onChange={setStatementDateTo}
                placeholder={t("mobile.chooseDate")}
                invalid={statementDateFrom > statementDateTo}
              />
            </>
          ) : null}
        </MobileFormSection>

        <MobileFormSection title={t("mobile.onStatement")}>
          <MobileFormAmountRow
            label={t("mobile.closingBalance")}
            value={statementBalance}
            onChange={setStatementBalance}
            currencyCode={currency}
          />
        </MobileFormSection>

        {/* Optional, so it sits apart with what it is for written under it. */}
        <MobileFormSection footer={`${t("mobile.optional")}. ${t("new.openingBalanceHint")}`}>
          <MobileFormAmountRow
            label={t("mobile.openingBalance")}
            value={statementOpeningBalance}
            onChange={setStatementOpeningBalance}
            currencyCode={currency}
          />
        </MobileFormSection>
      </MobileDetailScreen>
    )
  }

  return (
    <Dialog
      open={open && canEdit}
      onOpenChange={(nextOpen) => {
        if (nextOpen && !canEdit) return
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent size='md'>
        <DialogTitle>{t("new.title")}</DialogTitle>

        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <Label htmlFor='rec-account'>{t("new.account")}</Label>
            <Select
              id='rec-account'
              className='w-full justify-start'
              value={financeAccountId}
              onValueChange={setFinanceAccountId}
              placeholder={t("new.accountPlaceholder")}
              options={accounts.map((account) => ({ value: account.id, label: account.name }))}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>{t("new.period")}</Label>
            <FinancePeriodPicker
              className='w-full justify-start'
              value={preset}
              onValueChange={(next) => {
                setIsCustomPeriod(false)
                setPreset(next)
              }}
              allowCustom
              isCustom={isCustomPeriod}
              customLabel={`${statementDateFrom} → ${statementDateTo}`}
              onCustomSelect={() => setIsCustomPeriod(true)}
            />
          </div>

          {isCustomPeriod ? (
          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='rec-date-from'>{t("new.dateFrom")}</Label>
              <Input
                id='rec-date-from'
                type='date'
                value={statementDateFrom}
                onChange={(event) => setStatementDateFrom(event.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='rec-date-to'>{t("new.dateTo")}</Label>
              <Input
                id='rec-date-to'
                type='date'
                value={statementDateTo}
                onChange={(event) => setStatementDateTo(event.target.value)}
              />
            </div>
          </div>
          ) : null}

          <div className='space-y-1.5'>
            <Label htmlFor='rec-opening-balance'>{t("new.openingBalance")}</Label>
            <InputGroup className='w-full'>
              <InputGroupAddon align='inline-start'>
                <InputGroupText>{getCurrencyPrefix(currency)}</InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                id='rec-opening-balance'
                type='number'
                step='any'
                inputMode='decimal'
                value={statementOpeningBalance}
                onChange={(event) => setStatementOpeningBalance(event.target.value)}
              />
            </InputGroup>
            <p className='text-muted-foreground text-xs'>{t("new.openingBalanceHint")}</p>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='rec-statement-balance'>{t("new.statementBalance")}</Label>
            <InputGroup className='w-full'>
              <InputGroupAddon align='inline-start'>
                <InputGroupText>{getCurrencyPrefix(currency)}</InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                id='rec-statement-balance'
                type='number'
                step='any'
                inputMode='decimal'
                value={statementBalance}
                onChange={(event) => setStatementBalance(event.target.value)}
              />
            </InputGroup>
          </div>

          {accounts.length === 0 ? (
            <HelperMessage tone='info' size='sm'>
              {t("sessions.empty")}
            </HelperMessage>
          ) : null}
        </div>

        <DialogFooter className='gap-2'>
          <Button size='sm' variant='outline' onClick={() => onOpenChange(false)} disabled={createSession.isPending}>
            {t("dialogs.cancel")}
          </Button>
          <Button size='sm' onClick={() => void handleCreate()} disabled={!canEdit || !canSubmit || createSession.isPending}>
            {t("new.action")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
